import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { loginUrlWithNext, readNextParam } from '@/lib/safeNextPath';
import { AlertCircle, LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';

// Invite acceptance (/join) renders this page with ?token=&org= in the URL.
const readInvite = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    token: (params.get('token') || '').trim(),
    org: (params.get('org') || '').trim(),
  };
};

export default function Login() {
  const { login, isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [language, setLanguage] = useState<'is' | 'en'>('is');
  const [error, setError] = useState<string | null>(null);
  const invite = readInvite();
  const nextPath = readNextParam(window.location.search);

  // Check for error in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    if (errorParam) {
      setError(errorParam);
      // Clean URL (preserve the join path + invite params during invite acceptance)
      if (!invite.token) {
        window.history.replaceState({}, '', loginUrlWithNext(nextPath ?? '/'));
      }
    }
  }, [invite.token, nextPath]);

  // Load language preference from localStorage on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('loginLanguage') as 'is' | 'en' | null;
    if (savedLang) {
      setLanguage(savedLang);
    }
  }, []);

  // Save language preference to localStorage when it changes
  const handleLanguageChange = (lang: 'is' | 'en') => {
    setLanguage(lang);
    localStorage.setItem('loginLanguage', lang);
    // Also set for terms page
    localStorage.setItem('termsLanguage', lang);
  };

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      setLocation(nextPath ?? '/');
    }
  }, [isAuthenticated, setLocation, nextPath]);

  const content = {
    is: {
      divider: 'Innskráning',
      loginButton: 'Rafræn innskráning',
      inviteMessage: (org: string) =>
        org ? `Þér hefur verið boðið að ganga í ${org} í Skríbunni` : 'Þér hefur verið boðið að ganga í Skríbunni',
      termsText: 'Með því að skrá þig inn samþykkir þú',
      termsLink: 'skilmála fyrir notkun Skríbunnar',
      problemText: 'Vandamál með innskráningu?',
      contactLink: 'Hafðu samband',
      errors: {
        user_not_provisioned:
          'Þú ert ekki með aðgang að Skríbunni. Ef það passar ekki sendu okkur póst á hello@careflux.ai',
        auth_failed: 'Innskráning mistókst. Vinsamlegast reyndu aftur.',
        default: 'Villa kom upp við innskráningu.',
      },
    },
    en: {
      divider: 'Sign in',
      loginButton: 'Sign in with electronic ID',
      inviteMessage: (org: string) =>
        org ? `You have been invited to join ${org} on the Scribe` : 'You have been invited to join the Scribe',
      termsText: 'By signing in, you agree to the',
      termsLink: 'terms of use for the Scribe',
      problemText: 'Problems signing in?',
      contactLink: 'Contact us',
      errors: {
        user_not_provisioned:
          'You do not have access to the Scribe. If this is incorrect, please email us at hello@careflux.ai',
        auth_failed: 'Authentication failed. Please try again.',
        default: 'An error occurred during sign in.',
      },
    },
  };

  const getErrorMessage = (errorCode: string) => {
    const errors = content[language].errors as Record<string, string>;
    return errors[errorCode] || errors.default;
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-primary/5">
      <div className="w-full max-w-md p-8">
        {/* Language Toggle - Above the card */}
        <div className="mb-4 flex justify-end">
          <div className="inline-flex gap-1 rounded-lg border border-border bg-card p-1 shadow-sm">
            <button
              type="button"
              onClick={() => handleLanguageChange('is')}
              className={`inline-flex items-center justify-center rounded-md px-3 py-1.5 font-medium text-sm transition-all ${
                language === 'is'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }
              `}
            >
              <span className="mr-1">🇮🇸</span> IS
            </button>
            <button
              type="button"
              onClick={() => handleLanguageChange('en')}
              className={`inline-flex items-center justify-center rounded-md px-3 py-1.5 font-medium text-sm transition-all ${
                language === 'en'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }
              `}
            >
              <span className="mr-1">🇬🇧</span> EN
            </button>
          </div>
        </div>

        <div className="space-y-6 rounded-lg border border-border bg-card p-8 shadow-lg">
          {/* Logo */}
          <div className="text-center">
            <div className="flex justify-center">
              <img src="/careflux.png" alt="Careflux" className="h-36 w-auto" />
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{getErrorMessage(error)}</AlertDescription>
            </Alert>
          )}

          {/* Invite acceptance message (/join flow) */}
          {invite.token && <p className="text-center text-sm">{content[language].inviteMessage(invite.org)}</p>}

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-border border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{content[language].divider}</span>
            </div>
          </div>

          {/* Login Button */}
          <div className="space-y-4">
            <Button
              onClick={() => login(invite.token, nextPath)}
              className="h-12 w-full font-medium text-base"
              size="lg"
            >
              <LogIn className="mr-2 h-5 w-5" />
              {content[language].loginButton}
            </Button>
          </div>

          {/* Footer */}
          <div className="border-border border-t pt-4">
            <p className="text-center text-muted-foreground text-xs">
              {content[language].termsText}{' '}
              <a href="/terms" className="text-primary hover:underline">
                {content[language].termsLink}
              </a>
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-muted-foreground text-xs">
            {content[language].problemText}{' '}
            <a href="mailto:hello@careflux.ai" className="text-primary hover:underline">
              {content[language].contactLink}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
